// @docs:
//   - docs/wip/CREW_PAPER_REQUESTS.md  § Providing and sharing (upload)
// ==============================================
// Phone photos → a JPEG of at most 2500 px on the long edge, before upload.
// Re-encoding every image also drops its metadata (a camera photo carries the
// GPS position it was taken at) and turns PNG/WebP screenshots into one format.
// iOS usually hands a file input a JPEG even for HEIC photos; when a browser
// hands us HEIC and cannot decode it, we say so instead of uploading a file the
// skipper cannot open.
//
// The decoding and encoding sit behind ImageCodec so the rules here are tested
// without a canvas (jsdom has none); browserCodec is the real one.
// ==============================================

export const MAX_EDGE = 2500;
export const JPEG_QUALITY = 0.85;
/** The API refuses a requested paper over 25 MB (REQUEST_ITEM_MAX_BYTES). */
export const MAX_UPLOAD_BYTES = 25 * 1024 * 1024;

export type NormaliseCode = 'HEIC_UNSUPPORTED' | 'UNREADABLE' | 'UNSUPPORTED' | 'TOO_LARGE';
const MESSAGES: Record<NormaliseCode, string> = {
  HEIC_UNSUPPORTED: 'This photo is in HEIC format, which this browser can’t open. Choose a JPEG or PDF, or take the photo again with the camera.',
  UNREADABLE: 'This picture couldn’t be opened. Choose a JPEG, PNG or PDF, or take the photo again.',
  UNSUPPORTED: 'Choose a photo or a PDF.',
  TOO_LARGE: 'That file is over 25 MB. Choose a smaller PDF, or take a photo of the page instead.',
};

export class NormaliseError extends Error {
  constructor(public code: NormaliseCode) { super(MESSAGES[code]); this.name = 'NormaliseError'; }
}

export function fitWithin(width: number, height: number, max = MAX_EDGE): { width: number; height: number } {
  const long = Math.max(width, height);
  if (long <= max) return { width, height };
  const k = max / long;
  return { width: Math.round(width * k), height: Math.round(height * k) };
}

/** EXIF orientation 1–8 from a JPEG's APP1 segment; 1 (upright) when there is none. */
export function readJpegOrientation(buf: ArrayBuffer): number {
  const v = new DataView(buf);
  if (v.byteLength < 4 || v.getUint16(0) !== 0xffd8) return 1;
  let off = 2;
  while (off + 4 <= v.byteLength) {
    const marker = v.getUint16(off);
    if ((marker & 0xff00) !== 0xff00 || marker === 0xffda || marker === 0xffd9) return 1;
    const len = v.getUint16(off + 2);
    if (marker === 0xffe1 && off + 10 <= v.byteLength && v.getUint32(off + 4) === 0x45786966) {
      const tiff = off + 10;
      if (tiff + 8 > v.byteLength) return 1;
      const le = v.getUint16(tiff) === 0x4949;
      const ifd = tiff + v.getUint32(tiff + 4, le);
      if (ifd + 2 > v.byteLength) return 1;
      const n = v.getUint16(ifd, le);
      for (let i = 0; i < n; i++) {
        const e = ifd + 2 + i * 12;
        if (e + 10 > v.byteLength) return 1;
        if (v.getUint16(e, le) === 0x0112) {
          const o = v.getUint16(e + 8, le);
          return o >= 1 && o <= 8 ? o : 1;
        }
      }
      return 1;
    }
    off += 2 + len;
  }
  return 1;
}

/** The upright size of an image stored as width × height with this orientation. */
export function orientedSize(width: number, height: number, orientation: number): { width: number; height: number } {
  return orientation >= 5 ? { width: height, height: width } : { width, height };
}

/** ctx.setTransform arguments that draw a stored image upright on a canvas of W × H (already swapped). */
export function orientationTransform(orientation: number, W: number, H: number): [number, number, number, number, number, number] {
  switch (orientation) {
    case 2: return [-1, 0, 0, 1, W, 0];
    case 3: return [-1, 0, 0, -1, W, H];
    case 4: return [1, 0, 0, -1, 0, H];
    case 5: return [0, 1, 1, 0, 0, 0];
    case 6: return [0, 1, -1, 0, W, 0];
    case 7: return [0, -1, -1, 0, W, H];
    case 8: return [0, -1, 1, 0, 0, H];
    default: return [1, 0, 0, 1, 0, 0];
  }
}

export function classifyFile(f: { type: string; name: string }): 'pdf' | 'image' | 'heic' | 'unsupported' {
  const type = (f.type || '').toLowerCase();
  const ext = f.name.toLowerCase().split('.').pop() ?? '';
  if (type === 'application/pdf' || (!type && ext === 'pdf')) return 'pdf';
  if (type === 'image/heic' || type === 'image/heif' || ext === 'heic' || ext === 'heif') return 'heic';
  if (type.startsWith('image/') || (!type && ['jpg', 'jpeg', 'png', 'webp', 'gif'].includes(ext))) return 'image';
  return 'unsupported';
}

export function jpegName(name: string): string {
  const base = name.replace(/\.[^./\\]+$/, '') || 'photo';
  return `${base}.jpg`;
}

export interface DecodedImage {
  width: number;
  height: number;
  source: CanvasImageSource;
  /** the browser already applied the EXIF orientation (every current browser does for <img>) */
  oriented: boolean;
  close?: () => void;
}
export interface ImageCodec {
  decode(file: Blob): Promise<DecodedImage>;
  /** Draw upright at `target` (already in upright orientation) and encode as JPEG. */
  encode(img: DecodedImage, target: { width: number; height: number }, orientation: number, quality: number): Promise<Blob>;
}

async function headBytes(file: Blob, n = 256 * 1024): Promise<ArrayBuffer> {
  const part = file.slice(0, n);
  if (typeof part.arrayBuffer === 'function') return part.arrayBuffer();
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result as ArrayBuffer);
    r.onerror = () => reject(r.error);
    r.readAsArrayBuffer(part);
  });
}

/**
 * The file to upload for a requested paper: a PDF as it is (≤ 25 MB), any image
 * as an upright JPEG ≤ 2500 px. Throws NormaliseError with a message to show.
 */
export async function normaliseForUpload(file: File, codec: ImageCodec = browserCodec): Promise<File> {
  const kind = classifyFile(file);
  if (kind === 'unsupported') throw new NormaliseError('UNSUPPORTED');
  if (kind === 'pdf') {
    if (file.size > MAX_UPLOAD_BYTES) throw new NormaliseError('TOO_LARGE');
    return file;
  }
  let img: DecodedImage;
  try { img = await codec.decode(file); } catch { throw new NormaliseError(kind === 'heic' ? 'HEIC_UNSUPPORTED' : 'UNREADABLE'); }
  try {
    const orientation = img.oriented ? 1 : readJpegOrientation(await headBytes(file));
    const upright = orientedSize(img.width, img.height, orientation);
    const target = fitWithin(upright.width, upright.height);
    let blob: Blob;
    try { blob = await codec.encode(img, target, orientation, JPEG_QUALITY); } catch { throw new NormaliseError('UNREADABLE'); }
    if (blob.size > MAX_UPLOAD_BYTES) throw new NormaliseError('TOO_LARGE');
    return new File([blob], jpegName(file.name), { type: 'image/jpeg', lastModified: file.lastModified || Date.now() });
  } finally {
    img.close?.();
  }
}

/** The real codec: <img> decode (EXIF applied by the browser where supported) → canvas → JPEG. */
export const browserCodec: ImageCodec = {
  async decode(file) {
    const url = URL.createObjectURL(file);
    const el = new Image();
    el.decoding = 'async';
    try {
      await new Promise<void>((resolve, reject) => { el.onload = () => resolve(); el.onerror = () => reject(new Error('decode failed')); el.src = url; });
      if (!el.naturalWidth || !el.naturalHeight) throw new Error('empty image');
    } catch (e) { URL.revokeObjectURL(url); throw e; }
    const oriented = typeof CSS !== 'undefined' && typeof CSS.supports === 'function' && CSS.supports('image-orientation', 'from-image');
    return { width: el.naturalWidth, height: el.naturalHeight, source: el, oriented, close: () => URL.revokeObjectURL(url) };
  },
  async encode(img, target, orientation, quality) {
    const canvas = document.createElement('canvas');
    canvas.width = target.width; canvas.height = target.height;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('no canvas');
    ctx.fillStyle = '#ffffff'; // PNG transparency would turn black in a JPEG
    ctx.fillRect(0, 0, target.width, target.height);
    ctx.setTransform(...orientationTransform(orientation, target.width, target.height));
    const draw = orientation >= 5 ? { w: target.height, h: target.width } : { w: target.width, h: target.height };
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(img.source, 0, 0, draw.w, draw.h);
    return new Promise<Blob>((resolve, reject) => canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('encode failed'))), 'image/jpeg', quality));
  },
};
